/**
 * Passkey/WebAuthn API module.
 * Handles all passkey registration and authentication.
 */
import { api } from './client';
import type {
  PasskeyDevice,
  PasskeyRegistrationOptions,
  PasskeyAuthOptions,
  PasskeyAuthResponse,
  PasskeyRegisterResponse,
  InviteCodeInfo,
} from '../types';

// =============================================================================
// WebAuthn browser helpers
// =============================================================================

/**
 * Check if WebAuthn is supported in this browser.
 */
export function isWebAuthnSupported(): boolean {
  return !!(
    window.PublicKeyCredential &&
    typeof window.PublicKeyCredential === 'function'
  );
}

/**
 * Check if platform authenticator (Face ID, Touch ID, Windows Hello) is available.
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Base64url decode (WebAuthn uses base64url encoding).
 */
function base64urlToBuffer(base64url: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Base64url encode.
 */
function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// =============================================================================
// Registration (new users with invite codes)
// =============================================================================

/**
 * Validate an invite code.
 */
export async function validateInviteCode(code: string): Promise<{
  success: boolean;
  data?: { display_name: string; valid: boolean };
  error?: { code: string; message: string };
}> {
  return api.post('/passkey/register/validate-invite', { code });
}

/**
 * Get WebAuthn registration options for a new user.
 */
export async function getRegistrationOptions(inviteCode: string): Promise<{
  success: boolean;
  data?: PasskeyRegistrationOptions;
  error?: { code: string; message: string };
}> {
  return api.post('/passkey/register/options', { code: inviteCode });
}

/**
 * Complete passkey registration with the credential response.
 */
export async function completeRegistration(
  inviteCode: string,
  credential: PublicKeyCredential,
  deviceName: string
): Promise<{
  success: boolean;
  data?: PasskeyRegisterResponse;
  error?: { code: string; message: string };
}> {
  const attestationResponse = credential.response as AuthenticatorAttestationResponse;

  // Convert credential to JSON format for backend
  const credentialJSON = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(attestationResponse.clientDataJSON),
      attestationObject: bufferToBase64url(attestationResponse.attestationObject),
    },
    authenticatorAttachment: (credential as any).authenticatorAttachment,
  };

  return api.post('/passkey/register/complete', {
    invite_code: inviteCode,
    credential: credentialJSON,
    device_name: deviceName,
  });
}

/**
 * Full registration flow: get options -> create credential -> complete.
 */
export async function registerWithPasskey(
  inviteCode: string,
  deviceName: string = 'Primary Device'
): Promise<{
  success: boolean;
  data?: PasskeyRegisterResponse;
  error?: { code: string; message: string };
}> {
  // 1. Get registration options
  const optionsResult = await getRegistrationOptions(inviteCode);
  if (!optionsResult.success || !optionsResult.data) {
    return {
      success: false,
      error: optionsResult.error || { code: 'OPTIONS_FAILED', message: 'Failed to get registration options' },
    };
  }

  const { options } = optionsResult.data;

  // 2. Create credential using browser WebAuthn API
  try {
    const publicKeyOptions: PublicKeyCredentialCreationOptions = {
      rp: options.rp,
      user: {
        id: base64urlToBuffer(options.user.id),
        name: options.user.name,
        displayName: options.user.displayName,
      },
      challenge: base64urlToBuffer(options.challenge),
      pubKeyCredParams: options.pubKeyCredParams,
      timeout: options.timeout,
      excludeCredentials: options.excludeCredentials?.map(cred => ({
        type: cred.type,
        id: base64urlToBuffer(cred.id),
        transports: cred.transports as AuthenticatorTransport[],
      })),
      authenticatorSelection: options.authenticatorSelection,
      attestation: options.attestation,
    };

    const credential = await navigator.credentials.create({
      publicKey: publicKeyOptions,
    }) as PublicKeyCredential;

    if (!credential) {
      return {
        success: false,
        error: { code: 'CREDENTIAL_CANCELLED', message: 'Setup was cancelled' },
      };
    }

    // 3. Complete registration
    return completeRegistration(inviteCode, credential, deviceName);
  } catch (error) {
    console.error('WebAuthn registration error:', error);

    // Handle specific WebAuthn errors
    if (error instanceof DOMException) {
      if (error.name === 'NotAllowedError') {
        return {
          success: false,
          error: { code: 'NOT_ALLOWED', message: 'Setup was cancelled or timed out' },
        };
      }
      if (error.name === 'InvalidStateError') {
        return {
          success: false,
          error: { code: 'ALREADY_REGISTERED', message: 'This device is already registered' },
        };
      }
    }

    return {
      success: false,
      error: {
        code: 'WEBAUTHN_ERROR',
        message: error instanceof Error ? error.message : "Couldn't finish setup",
      },
    };
  }
}

// =============================================================================
// Authentication (existing users)
// =============================================================================

/**
 * Get WebAuthn authentication options.
 */
export async function getAuthenticationOptions(username?: string): Promise<{
  success: boolean;
  data?: PasskeyAuthOptions;
  error?: { code: string; message: string };
}> {
  const params = username ? { username } : {};
  return api.post('/passkey/auth/options', params);
}

/**
 * Verify authentication response.
 */
export async function verifyAuthentication(
  challengeId: string,
  credential: PublicKeyCredential
): Promise<{
  success: boolean;
  data?: PasskeyAuthResponse;
  error?: { code: string; message: string };
}> {
  const assertionResponse = credential.response as AuthenticatorAssertionResponse;

  // Convert credential to JSON format for backend
  const credentialJSON = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(assertionResponse.clientDataJSON),
      authenticatorData: bufferToBase64url(assertionResponse.authenticatorData),
      signature: bufferToBase64url(assertionResponse.signature),
      userHandle: assertionResponse.userHandle
        ? bufferToBase64url(assertionResponse.userHandle)
        : null,
    },
  };

  return api.post('/passkey/auth/verify', {
    challenge_id: challengeId,
    credential: credentialJSON,
  });
}

/**
 * Full authentication flow: get options -> get credential -> verify.
 */
export async function authenticateWithPasskey(username?: string): Promise<{
  success: boolean;
  data?: PasskeyAuthResponse;
  error?: { code: string; message: string };
}> {
  // 1. Get authentication options
  const optionsResult = await getAuthenticationOptions(username);
  if (!optionsResult.success || !optionsResult.data) {
    return {
      success: false,
      error: optionsResult.error || { code: 'OPTIONS_FAILED', message: 'Failed to get authentication options' },
    };
  }

  const { options, challenge_id } = optionsResult.data;

  // 2. Get credential using browser WebAuthn API
  try {
    const publicKeyOptions: PublicKeyCredentialRequestOptions = {
      challenge: base64urlToBuffer(options.challenge),
      timeout: options.timeout,
      rpId: options.rpId,
      allowCredentials: options.allowCredentials?.map(cred => ({
        type: cred.type,
        id: base64urlToBuffer(cred.id),
        transports: cred.transports as AuthenticatorTransport[],
      })),
      userVerification: options.userVerification,
    };

    const credential = await navigator.credentials.get({
      publicKey: publicKeyOptions,
    }) as PublicKeyCredential;

    if (!credential) {
      return {
        success: false,
        error: { code: 'CREDENTIAL_CANCELLED', message: 'Authentication was cancelled' },
      };
    }

    // 3. Verify with backend
    return verifyAuthentication(challenge_id, credential);
  } catch (error) {
    console.error('WebAuthn authentication error:', error);

    // Handle specific WebAuthn errors
    if (error instanceof DOMException) {
      if (error.name === 'NotAllowedError') {
        return {
          success: false,
          error: { code: 'NOT_ALLOWED', message: 'Authentication was cancelled or timed out' },
        };
      }
    }

    return {
      success: false,
      error: {
        code: 'WEBAUTHN_ERROR',
        message: error instanceof Error ? error.message : 'Authentication failed',
      },
    };
  }
}

/**
 * Unlock Only Me with a passkey (Face ID / fingerprint / device passcode).
 * Same options → credential → verify dance as login, but against the vault
 * unlock endpoints: success grants this session's short-lived unlock instead
 * of minting a session. Also the forgot-my-code path — a passkey proves the
 * account owner, so it works even during a code lockout.
 */
export async function unlockVaultWithPasskey(): Promise<{
  success: boolean;
  error?: { code: string; message: string };
}> {
  const optionsResult = (await api.post('/vault/unlock/passkey/options', {})) as {
    success: boolean;
    data?: { options: PasskeyAuthOptions['options']; challenge_id: string };
    error?: { code: string; message: string };
  };
  if (!optionsResult.success || !optionsResult.data) {
    return {
      success: false,
      error: optionsResult.error || { code: 'OPTIONS_FAILED', message: 'Failed to get unlock options' },
    };
  }

  const { options, challenge_id } = optionsResult.data;

  try {
    const publicKeyOptions: PublicKeyCredentialRequestOptions = {
      challenge: base64urlToBuffer(options.challenge),
      timeout: options.timeout,
      rpId: options.rpId,
      allowCredentials: options.allowCredentials?.map(cred => ({
        type: cred.type,
        id: base64urlToBuffer(cred.id),
        transports: cred.transports as AuthenticatorTransport[],
      })),
      userVerification: options.userVerification,
    };

    const credential = await navigator.credentials.get({
      publicKey: publicKeyOptions,
    }) as PublicKeyCredential;

    if (!credential) {
      return {
        success: false,
        error: { code: 'CREDENTIAL_CANCELLED', message: 'Unlock was cancelled' },
      };
    }

    const assertionResponse = credential.response as AuthenticatorAssertionResponse;
    return api.post('/vault/unlock/passkey/verify', {
      challenge_id,
      credential: {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(assertionResponse.clientDataJSON),
          authenticatorData: bufferToBase64url(assertionResponse.authenticatorData),
          signature: bufferToBase64url(assertionResponse.signature),
          userHandle: assertionResponse.userHandle
            ? bufferToBase64url(assertionResponse.userHandle)
            : null,
        },
      },
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotAllowedError') {
      return {
        success: false,
        error: { code: 'NOT_ALLOWED', message: 'Unlock was cancelled or timed out' },
      };
    }
    return {
      success: false,
      error: {
        code: 'WEBAUTHN_ERROR',
        message: error instanceof Error ? error.message : 'Unlock failed',
      },
    };
  }
}

// =============================================================================
// Device management
// =============================================================================

/**
 * List all passkey devices for the current user.
 */
export async function listDevices(): Promise<{
  success: boolean;
  data?: PasskeyDevice[];
  error?: { code: string; message: string };
}> {
  return api.get('/passkey/devices');
}

/**
 * Add a new passkey device.
 */
export async function addDevice(deviceName: string): Promise<{
  success: boolean;
  data?: PasskeyDevice;
  error?: { code: string; message: string };
}> {
  // 1. Get registration options
  const optionsResult = await api.post<{ options: any }>('/passkey/devices/add/options', { device_name: deviceName });
  if (!optionsResult.success || !optionsResult.data) {
    return {
      success: false,
      error: (optionsResult as any).error || { code: 'OPTIONS_FAILED', message: 'Failed to get options' },
    };
  }

  const { options } = optionsResult.data;

  // 2. Create credential
  try {
    const publicKeyOptions: PublicKeyCredentialCreationOptions = {
      rp: options.rp,
      user: {
        id: base64urlToBuffer(options.user.id),
        name: options.user.name,
        displayName: options.user.displayName,
      },
      challenge: base64urlToBuffer(options.challenge),
      pubKeyCredParams: options.pubKeyCredParams,
      timeout: options.timeout,
      excludeCredentials: options.excludeCredentials?.map((cred: any) => ({
        type: cred.type,
        id: base64urlToBuffer(cred.id),
        transports: cred.transports,
      })),
      authenticatorSelection: options.authenticatorSelection,
      attestation: options.attestation,
    };

    const credential = await navigator.credentials.create({
      publicKey: publicKeyOptions,
    }) as PublicKeyCredential;

    if (!credential) {
      return {
        success: false,
        error: { code: 'CANCELLED', message: 'Device registration cancelled' },
      };
    }

    // 3. Complete
    const attestationResponse = credential.response as AuthenticatorAttestationResponse;
    const credentialJSON = {
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64url(attestationResponse.clientDataJSON),
        attestationObject: bufferToBase64url(attestationResponse.attestationObject),
      },
    };

    return api.post('/passkey/devices/add/complete', {
      credential: credentialJSON,
      device_name: deviceName,
    });
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'WEBAUTHN_ERROR',
        message: error instanceof Error ? error.message : 'Failed to add device',
      },
    };
  }
}

/**
 * Revoke a passkey device.
 */
export async function revokeDevice(deviceId: number): Promise<{
  success: boolean;
  error?: { code: string; message: string };
}> {
  return api.delete(`/passkey/devices/${deviceId}`);
}

// =============================================================================
// Recovery
// =============================================================================

/**
 * Generate new recovery codes (invalidates old ones).
 */
export async function generateRecoveryCodes(): Promise<{
  success: boolean;
  data?: { recovery_codes: string[]; message: string };
  error?: { code: string; message: string };
}> {
  return api.post('/passkey/recovery/new-codes');
}

/**
 * How many one-time codes remain unused (FEAT-013 — powers the Settings nudge).
 */
export async function getRecoveryStatus(): Promise<{
  success: boolean;
  data?: { unused_count: number };
  error?: { code: string; message: string };
}> {
  return api.get('/passkey/recovery/status');
}

/**
 * Use a recovery code to get a session.
 */
export async function useRecoveryCode(
  username: string,
  code: string
): Promise<{
  success: boolean;
  data?: PasskeyAuthResponse & { message: string };
  error?: { code: string; message: string };
}> {
  return api.post('/passkey/recovery/use', { username, code });
}

// =============================================================================
// Invite codes (any user)
// =============================================================================

/**
 * Create an invite code for a friend.
 * Any authenticated user can create invites.
 */
export async function createInviteCode(
  displayName: string,
  expiresHours: number = 48
): Promise<{
  success: boolean;
  data?: InviteCodeInfo & { created_by: string };
  error?: { code: string; message: string };
}> {
  return api.post('/passkey/invite', {
    display_name: displayName,
    expires_hours: expiresHours,
  });
}

/**
 * Delete an unused invite code.
 */
export async function deleteInviteCode(code: string): Promise<{
  success: boolean;
  error?: { code: string; message: string };
}> {
  return api.delete(`/passkey/invite/${code}`);
}

/**
 * List invite codes I've created.
 */
export async function listMyInvites(): Promise<{
  success: boolean;
  data?: Array<{
    code: string;
    display_name: string;
    is_used: boolean;
    expires_at: string;
    created_at: string;
    used_by?: { id: number; username: string; display_name: string };
  }>;
  error?: { code: string; message: string };
}> {
  return api.get('/passkey/invites/mine');
}

// =============================================================================
// Admin (invite codes)
// =============================================================================

/**
 * List all invite codes (admin only).
 */
export async function listAllInviteCodes(): Promise<{
  success: boolean;
  data?: Array<InviteCodeInfo & { is_used: boolean; created_at: string }>;
  error?: { code: string; message: string };
}> {
  return api.get('/passkey/admin/invites');
}
